import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { RespondentColumnDetails, RespondentColumnDetailsDTO } from "../types/respondentColumnDetails";
/* eslint-disable import/export */
export abstract class DeleteColumnRPC implements
  LeoRPC<DeleteColumnRPC.Request, DeleteColumnRPC.Response, DeleteColumnRPC.Errors.Errors>
{
  abstract execute(
    request: DeleteColumnRPC.Request
  ): Promise<LeoRPCResult<DeleteColumnRPC.Response, DeleteColumnRPC.Errors.Errors>>;
}

export namespace DeleteColumnRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    respondentColumnDetails: RespondentColumnDetailsDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isDeleteColumnRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly respondentColumnDetails: RespondentColumnDetails;
  
  
    constructor(
    projectId: LeoUUID, 
    respondentColumnDetails: RespondentColumnDetails
    ) {
      this.projectId = projectId;
      this.respondentColumnDetails = respondentColumnDetails;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const respondentColumnDetails = RespondentColumnDetails.fromDTO(getObject(dto, "respondentColumnDetails", InvalidRequestRespondentColumnDetailsError));
      return new Request(
        projectId,
        respondentColumnDetails
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          respondentColumnDetails: this.respondentColumnDetails.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `DeleteColumnRPC(projectId='${this.projectId}',respondentColumnDetails='${this.respondentColumnDetails}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `DeleteColumnRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidRespondentColumn = "INVALID_RESPONDENT_COLUMN",
    ProjectArchived = "PROJECT_ARCHIVED",
    ProjectUniqueColumnCannotBeDeleted = "PROJECT_UNIQUE_COLUMN_CANNOT_BE_DELETED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `DeleteColumnRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidRespondentColumnDTO {
      code: string;
    }
    
    export class InvalidRespondentColumn extends LeoRPCError<RPCError> {
      readonly isInvalidRespondentColumn: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidRespondentColumn;
        super(code);
      }
    
    
      toDTO(): InvalidRespondentColumnDTO {
        const dto: InvalidRespondentColumnDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidRespondentColumn.prototype.toString = function toString(): string {
      return `DeleteColumnRPC.INVALID_RESPONDENT_COLUMN()`;
    };
    
    
    export interface ProjectArchivedDTO {
      code: string;
    }
    
    export class ProjectArchived extends LeoRPCError<RPCError> {
      readonly isProjectArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectArchived;
        super(code);
      }
    
    
      toDTO(): ProjectArchivedDTO {
        const dto: ProjectArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectArchived.prototype.toString = function toString(): string {
      return `DeleteColumnRPC.PROJECT_ARCHIVED()`;
    };
    
    
    export interface ProjectUniqueColumnCannotBeDeletedDTO {
      code: string;
    }
    
    export class ProjectUniqueColumnCannotBeDeleted extends LeoRPCError<RPCError> {
      readonly isProjectUniqueColumnCannotBeDeleted: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectUniqueColumnCannotBeDeleted;
        super(code);
      }
    
    
      toDTO(): ProjectUniqueColumnCannotBeDeletedDTO {
        const dto: ProjectUniqueColumnCannotBeDeletedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectUniqueColumnCannotBeDeleted.prototype.toString = function toString(): string {
      return `DeleteColumnRPC.PROJECT_UNIQUE_COLUMN_CANNOT_BE_DELETED()`;
    };
    

    export type Errors = InvalidProjectId | InvalidRespondentColumn | ProjectArchived | ProjectUniqueColumnCannotBeDeleted;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidRespondentColumnDTO | ProjectArchivedDTO | ProjectUniqueColumnCannotBeDeletedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidRespondentColumn:
          return new InvalidRespondentColumn();
        case RPCError.ProjectArchived:
          return new ProjectArchived();
        case RPCError.ProjectUniqueColumnCannotBeDeleted:
          return new ProjectUniqueColumnCannotBeDeleted();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends DeleteColumnRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestRespondentColumnDetailsError extends DeleteColumnRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespondentColumnDetailsError";
  }
}


export class InvalidResponseInvalidProjectIdError extends DeleteColumnRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidRespondentColumnError extends DeleteColumnRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidRespondentColumnError";
  }
}

export class InvalidResponseProjectArchivedError extends DeleteColumnRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectArchivedError";
  }
}

export class InvalidResponseProjectUniqueColumnCannotBeDeletedError extends DeleteColumnRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectUniqueColumnCannotBeDeletedError";
  }
}

